/* ══════════════════════════════════════════════════════════════════════
   NETWORK GRAPH — the progressive-disclosure view of the access data.

   Where Policy Insights lays one application out in full, this opens on
   the applications alone and reveals one level per click, seven deep:

     App -> Application -> Account -> Users|Groups|Roles (n)
         -> User|Group|Role -> Policy | Group member -> Policy

   ── Why vis-network is driven from a ref ────────────────────────────
   react-graph-vis hands vis a brand-new graph object whenever a prop
   changes, so every expand threw the whole dataset away and re-solved it
   from scratch — which is what made the graph dance. Here the nodes and
   edges live in vis-data DataSets that are mutated in place, and four
   things keep an expand still:

     1. add()/remove() on the DataSet, never a fresh graph object.
     2. Every node already on screen is pinned to its current coordinates
        before the children arrive, so the solver has only the new nodes
        left to place.
     3. Those children are seeded ON the clicked node and fanned out, so
        they grow from the click instead of flying in from the origin.
     4. Physics is switched off the moment it settles, and only switched
        back on for the length of one expand. Stabilization runs 200
        iterations, not thousands.

   Nothing here refetches: buildPagNetworkModel hands over the collapsed
   view and the full graph together, and expanding is a lookup.
   ══════════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { RotateCcw } from "lucide-react";
import { DataSet } from "vis-data";
import { Network, type Edge, type Node } from "vis-network";
import "vis-network/styles/vis-network.css";
import {
  ChartEmptyState,
  ChartLoadingState,
} from "@/app/components/ChartsComponent/ChartStates";
import {
  type PagNetEdge,
  type PagNetNode,
  type PagNetworkModel,
  type PagNodeKind,
  type PagSeverity,
} from "../../data/pagTypes";
import { NetworkLegend } from "./NetworkLegend";
import {
  GraphFullscreenControl,
  GraphZoomControls,
  useGraphFullscreen,
} from "./GraphControls";
import {
  buildOptions,
  EDGE_WIDTH,
  EDGE_WIDTH_DIM,
  FIT_ANIMATION,
  NAV_CSS,
  NODE_OPACITY,
  SPAWN_RADIUS,
  SURFACE,
  toVisEdge,
  toVisNode,
  type NetVisNode,
} from "./networkGraphVisuals";

interface NetworkGraphProps {
  model: PagNetworkModel;
  /** Reserved for when the access payload arrives over the wire; nothing
   *  in this component fetches. */
  loading?: boolean;
}

/** Lookups over the full graph, rebuilt only when the dataset changes. */
interface NetIndex {
  byId: Map<string, PagNetNode>;
  childrenOf: Map<string, string[]>;
  edgeById: Map<string, PagNetEdge>;
}

const GRAPH_HEIGHT = "62vh";
const STATE_HEIGHT = 300;

export function NetworkGraph({ model, loading = false }: NetworkGraphProps) {
  const { theme } = useTheme();
  const isDark = theme !== "light";
  const surface = isDark ? SURFACE.dark : SURFACE.light;

  const containerRef = useRef<HTMLDivElement>(null);
  const netRef = useRef<Network | null>(null);
  const nodesRef = useRef<DataSet<Node> | null>(null);
  const edgesRef = useRef<DataSet<Edge> | null>(null);

  /* Mirrors the live physics flag: setOptions restarts the solver, so it
     is only ever called on a real change. */
  const physicsOnRef = useRef(true);
  /* Armed by an expand and read by `stabilized`, so the camera moves after
     a real drill-down rather than on every physics tick. */
  const armedRef = useRef(false);
  const relatedRef = useRef<string[]>([]);
  const focusedRef = useRef<string | null>(null);
  const hiddenIdsRef = useRef<Set<string>>(new Set());
  const hiddenAppliedRef = useRef(false);
  const appliedDarkRef = useRef<boolean | null>(null);
  const fullscreenReadyRef = useRef(false);

  const surfaceRef = useRef(surface);
  surfaceRef.current = surface;

  /* The real Fullscreen API, same as Policy Insights. The `fixed inset-0`
     overlay this used before was positioned against the page's animated
     motion.div rather than the viewport, so it filled that box and never
     actually went fullscreen. */
  const { isFullscreen, toggle: toggleFullscreen } = useGraphFullscreen(containerRef);
  const [hasFocus, setHasFocus] = useState(false);
  const [hiddenKinds, setHiddenKinds] = useState<Set<PagNodeKind>>(() => new Set());
  const [hiddenRisks, setHiddenRisks] = useState<Set<PagSeverity>>(() => new Set());

  const index = useMemo<NetIndex>(() => {
    const byId = new Map(model.fullNodes.map((n) => [n.id, n]));
    const childrenOf = new Map<string, string[]>();
    const edgeById = new Map<string, PagNetEdge>();
    model.fullEdges.forEach((e) => {
      edgeById.set(e.id, e);
      const kids = childrenOf.get(e.from);
      if (kids) kids.push(e.to);
      else childrenOf.set(e.from, [e.to]);
    });
    return { byId, childrenOf, edgeById };
  }, [model]);

  const indexRef = useRef(index);
  indexRef.current = index;

  const isEmpty = model.fullNodes.length <= 1;

  /* ── Primitives ───────────────────────────────────────────────────── */

  const setPhysics = useCallback((on: boolean) => {
    if (physicsOnRef.current === on) return;
    physicsOnRef.current = on;
    netRef.current?.setOptions({ physics: { enabled: on } });
  }, []);

  /** A model node dressed for the canvas, honouring the legend filter so a
   *  node revealed while a filter is on does not sneak past it. */
  const makeNode = useCallback((n: PagNetNode): NetVisNode => {
    const childCount = (indexRef.current.childrenOf.get(n.id) ?? []).length;
    return {
      ...toVisNode(n, surfaceRef.current, childCount),
      hidden: hiddenIdsRef.current.has(n.id),
    };
  }, []);

  const fitRelated = useCallback(() => {
    const net = netRef.current;
    const nodes = nodesRef.current;
    if (!net || !nodes) return;
    const ids = relatedRef.current.filter((id) => nodes.get(id));
    net.fit(ids.length ? { nodes: ids, animation: FIT_ANIMATION } : { animation: FIT_ANIMATION });
  }, []);

  /** Run once the solver has settled, from whichever of the two events
   *  vis reaches first: `stabilizationIterationsDone` after a blocking
   *  stabilization run, `stabilized` after a live simulation. Either way
   *  physics goes off, and the camera moves only if an expand armed it. */
  const settle = useCallback(() => {
    setPhysics(false);
    if (!armedRef.current) return;
    armedRef.current = false;
    fitRelated();
  }, [fitRelated, setPhysics]);

  /** Edges touching the focused node stay lit; the rest fade back so the
   *  expansion reads as a drill-down rather than as more clutter. */
  const paintEdges = useCallback(() => {
    const edges = edgesRef.current;
    if (!edges) return;
    const focus = focusedRef.current;
    /* Both passes now come from the theme. The dim one used to be a fixed
       "lightgray" at 0.1, which is invisible on either canvas -- on the
       dark one because it is barely painted, on the light one because a
       pale grey at a tenth opacity over white is white. */
    const { edge: lit, edgeDim: dim } = surfaceRef.current;

    edges.update(
      edges.get().map((e) => {
        const on = !focus || String(e.from) === focus || String(e.to) === focus;
        return on
          ? { id: e.id as string, color: { color: lit, opacity: 1 }, width: EDGE_WIDTH }
          : {
              id: e.id as string,
              color: { color: dim, opacity: 1 },
              width: EDGE_WIDTH_DIM,
            };
      }),
    );
  }, []);

  /** vis has no zoomIn/zoomOut of its own -- scale is read and rewritten. */
  const zoomBy = useCallback((factor: number) => {
    const net = netRef.current;
    if (!net) return;
    net.moveTo({ scale: net.getScale() * factor, animation: FIT_ANIMATION });
  }, []);

  const fitAll = useCallback(() => {
    netRef.current?.fit({ animation: FIT_ANIMATION });
  }, []);

  /* ── Expand ───────────────────────────────────────────────────────── */

  const focusNode = useCallback(
    (nodeId: string) => {
      const net = netRef.current;
      const nodes = nodesRef.current;
      const edges = edgesRef.current;
      if (!net || !nodes || !edges) return;

      const { byId, childrenOf, edgeById } = indexRef.current;
      const clicked = byId.get(nodeId);
      if (!clicked) return;

      /* 1 — Pin everything already placed. The solver is then left with
         only the new arrivals, which is the whole anti-jitter trick. */
      const pos = net.getPositions();
      const pinned = nodes
        .getIds()
        .map(String)
        .filter((id) => pos[id])
        .map((id) => ({ id, x: pos[id].x, y: pos[id].y, fixed: { x: true, y: true } }));
      if (pinned.length) nodes.update(pinned);

      /* 2 — The related set: the clicked node plus one hop, counted across
         BOTH the visible graph (so its parent survives) and the full graph
         (so its children arrive). */
      const related = new Set<string>([nodeId]);
      if (clicked.parentId) related.add(clicked.parentId);
      edges.get().forEach((e) => {
        if (String(e.from) === nodeId) related.add(String(e.to));
        else if (String(e.to) === nodeId) related.add(String(e.from));
      });
      (childrenOf.get(nodeId) ?? []).forEach((id) => related.add(id));

      /* 3 — Everything else leaves, and its edges leave with it: an edge
         whose endpoint is gone has nothing to draw between. */
      const doomed = nodes
        .getIds()
        .map(String)
        .filter((id) => !related.has(id));
      if (doomed.length) {
        const gone = new Set(doomed);
        const orphans = edges
          .get()
          .filter((e) => gone.has(String(e.from)) || gone.has(String(e.to)))
          .map((e) => e.id as string);
        if (orphans.length) edges.remove(orphans);
        nodes.remove(doomed);
      }

      /* 4 — Whatever the related set is still missing is seeded AT the
         clicked node and fanned outward. This is also what walks back UP:
         the grandparent is one hop from the parent you just clicked. */
      const here = pos[nodeId] ?? { x: 0, y: 0 };
      const fresh = [...related].filter(
        (id) => id !== nodeId && byId.has(id) && !nodes.get(id),
      );
      const spawned = fresh.map((id, i) => {
        const angle = (2 * Math.PI * i) / fresh.length;
        return {
          ...makeNode(byId.get(id) as PagNetNode),
          x: here.x + SPAWN_RADIUS * Math.cos(angle),
          y: here.y + SPAWN_RADIUS * Math.sin(angle),
        };
      });
      const links = fresh
        .map((id) => edgeById.get(`${nodeId}-${id}`) ?? edgeById.get(`${id}-${nodeId}`))
        .filter((e): e is PagNetEdge => !!e && !edges.get(e.id))
        .map((e) => ({
          ...toVisEdge(e),
          hidden: hiddenIdsRef.current.has(e.from) || hiddenIdsRef.current.has(e.to),
        }));

      focusedRef.current = nodeId;
      relatedRef.current = [...related];
      setHasFocus(true);

      if (spawned.length) {
        /* Armed BEFORE the data lands: adding nodes is what starts the
           solver, and the settle handler reads this flag. */
        armedRef.current = true;
        // Physics back on for the settle only; the handler switches it off.
        setPhysics(true);
        nodes.add(spawned);
      }
      if (links.length) edges.add(links);
      paintEdges();

      // Nothing new to solve means no settle event is coming.
      if (!spawned.length) fitRelated();
    },
    [fitRelated, makeNode, paintEdges, setPhysics],
  );

  const resetView = useCallback(() => {
    const nodes = nodesRef.current;
    const edges = edgesRef.current;
    if (!nodes || !edges) return;

    edges.clear();
    nodes.clear();
    nodes.add(model.initialNodes.map(makeNode));
    edges.add(model.initialEdges.map(toVisEdge));

    focusedRef.current = null;
    relatedRef.current = [];
    armedRef.current = false;
    setHasFocus(false);
    paintEdges();
    setPhysics(true);
  }, [makeNode, model, paintEdges, setPhysics]);

  /* ── The network itself ───────────────────────────────────────────── */

  useEffect(() => {
    const el = containerRef.current;
    if (!el || isEmpty) return;

    const nodes = new DataSet<Node>(model.initialNodes.map(makeNode));
    const edges = new DataSet<Edge>(model.initialEdges.map(toVisEdge));
    const net = new Network(el, { nodes, edges }, buildOptions(surfaceRef.current, true));

    netRef.current = net;
    nodesRef.current = nodes;
    edgesRef.current = edges;
    physicsOnRef.current = true;
    armedRef.current = false;
    relatedRef.current = [];
    focusedRef.current = null;
    appliedDarkRef.current = surfaceRef.current === SURFACE.dark;

    net.on("stabilizationIterationsDone", settle);
    net.on("stabilized", settle);
    net.on("selectNode", (params: { nodes?: (string | number)[] }) => {
      const id = params?.nodes?.[0];
      if (id !== undefined) focusNode(String(id));
    });
    net.on("hoverNode", (params: { node: string | number }) => {
      el.style.cursor = "pointer";
      nodes.update({ id: params.node, opacity: 1, zIndex: 10 } as NetVisNode);
    });
    net.on("blurNode", (params: { node: string | number }) => {
      el.style.cursor = "default";
      nodes.update({ id: params.node, opacity: NODE_OPACITY, zIndex: 0 } as NetVisNode);
    });

    return () => {
      net.destroy();
      netRef.current = null;
      nodesRef.current = null;
      edgesRef.current = null;
      el.style.cursor = "default";
    };
  }, [focusNode, isEmpty, makeNode, model, settle]);

  /* Theme moves the surfaces, not the layout: the nodes are re-imaged in
     place so a light/dark switch never costs the user their expansion. */
  useEffect(() => {
    const net = netRef.current;
    const nodes = nodesRef.current;
    if (!net || !nodes) return;
    if (appliedDarkRef.current === isDark) return;
    appliedDarkRef.current = isDark;

    net.setOptions(buildOptions(surface, physicsOnRef.current));
    nodes.update(
      nodes
        .getIds()
        .map(String)
        .map((id) => {
          const n = indexRef.current.byId.get(id);
          if (!n) return { id };
          const next = toVisNode(n, surface, (indexRef.current.childrenOf.get(id) ?? []).length);
          return { id, image: next.image, font: next.font };
        }),
    );
    paintEdges();
  }, [isDark, paintEdges, surface]);

  /* Legend filter. Hiding a kind takes its whole subtree with it, and any
     edge that touches a hidden node goes quiet too. */
  useEffect(() => {
    const hidden = new Set<string>();

    if (hiddenKinds.size) {
      const stack = model.fullNodes.filter((n) => hiddenKinds.has(n.kind)).map((n) => n.id);
      while (stack.length) {
        const id = stack.pop() as string;
        if (hidden.has(id)) continue;
        hidden.add(id);
        (index.childrenOf.get(id) ?? []).forEach((child) => stack.push(child));
      }
    }
    if (hiddenRisks.size) {
      model.fullNodes.forEach((n) => {
        if (n.type === "policy" && hiddenRisks.has(n.severity ?? "Uncategorized")) {
          hidden.add(n.id);
        }
      });
    }
    hiddenIdsRef.current = hidden;

    const nodes = nodesRef.current;
    const edges = edgesRef.current;
    if (!nodes || !edges) return;

    /* Nothing hidden and nothing was hidden before means there is nothing
       to write. Skipping matters: a DataSet write restarts the solver, and
       this effect also runs on mount, mid-stabilization. */
    if (!hidden.size && !hiddenAppliedRef.current) return;
    hiddenAppliedRef.current = hidden.size > 0;

    nodes.update(
      nodes
        .getIds()
        .map(String)
        .map((id) => ({ id, hidden: hidden.has(id) })),
    );
    edges.update(
      edges.get().map((e) => ({
        id: e.id as string,
        hidden: hidden.has(String(e.from)) || hidden.has(String(e.to)),
      })),
    );
  }, [hiddenKinds, hiddenRisks, index, model]);

  /* Fullscreen is a CSS overlay rather than the Fullscreen API, so the
     same canvas and the same network instance carry over — only the box
     around them changes, then the camera refits into it. */
  useEffect(() => {
    if (!fullscreenReadyRef.current) {
      fullscreenReadyRef.current = true;
      return;
    }
    const net = netRef.current;
    const timer = setTimeout(() => {
      net?.redraw();
      fitRelated();
    }, 80);

    /* No Escape handler: requestFullscreen hands that to the browser,
       which exits and fires fullscreenchange for the hook to pick up. */
    return () => clearTimeout(timer);
  }, [fitRelated, isFullscreen]);

  /* ── Legend toggles ───────────────────────────────────────────────── */

  const toggleKind = useCallback((kind: PagNodeKind) => {
    setHiddenKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }, []);

  const toggleRisk = useCallback((risk: PagSeverity) => {
    setHiddenRisks((prev) => {
      const next = new Set(prev);
      if (next.has(risk)) next.delete(risk);
      else next.add(risk);
      return next;
    });
  }, []);

  /* ── Render ───────────────────────────────────────────────────────── */

  return (
    <div
      className={`pag-net relative w-full overflow-hidden ${
        isDark ? "pag-net-dark" : ""
      } ${isFullscreen ? "" : "rounded-lg"}`}
      style={{
        /* 100% rather than 100vh in fullscreen: the element IS the screen
           once requestFullscreen takes it, so a viewport unit here would
           only re-state its own height. */
        height: isFullscreen ? "100%" : GRAPH_HEIGHT,
        minHeight: isFullscreen ? undefined : 460,
        background: surface.canvas,
        border: isFullscreen ? "none" : `1px solid ${surface.panelBorder}`,
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: NAV_CSS }} />

      <div ref={containerRef} className="size-full" />

      <GraphFullscreenControl
        isFullscreen={isFullscreen}
        onToggle={toggleFullscreen}
        isDark={isDark}
      />

      {/* Zoom / fit, bottom left — the same cluster Policy Insights wears,
          replacing the arrow pad vis drew bottom-right in its own style. */}
      <GraphZoomControls
        isDark={isDark}
        onZoomIn={() => zoomBy(1.25)}
        onZoomOut={() => zoomBy(0.8)}
        onFit={fitAll}
      />

      {/* Reset view is this graph's alone — Policy Insights lays its tree
          out in full and has nothing to drill back out of. It sits beside
          the fullscreen button rather than in the zoom cluster, which is
          shared and must stay identical across both. */}
      {hasFocus && (
        <button
          type="button"
          onClick={resetView}
          className="absolute left-12 top-3 z-10 flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-indigo-500 transition-colors hover:bg-indigo-500/10"
          style={{
            border: `1px solid ${surface.panelBorder}`,
            background: isDark ? "#151E31" : "#FFFFFF",
          }}
        >
          <RotateCcw className="size-3" />
          Reset view
        </button>
      )}

      <NetworkLegend
        surface={surface}
        hiddenKinds={hiddenKinds}
        hiddenRisks={hiddenRisks}
        onToggleKind={toggleKind}
        onToggleRisk={toggleRisk}
      />

      {/* The canvas is never left blank: a state always covers it. */}
      {(loading || isEmpty) && (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center px-4"
          style={{ background: surface.canvas }}
        >
          {loading ? (
            <ChartLoadingState height={STATE_HEIGHT} shape="radial" />
          ) : (
            <ChartEmptyState
              height={STATE_HEIGHT}
              shape="radial"
              message="No applications carry access data yet, so there is nothing to map."
            />
          )}
        </div>
      )}
    </div>
  );
}
